import React, { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import { addBudgetCostItem, deleteBudgetCostItem, updateBudgetCostItem } from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';
import { PartyFields, PartyHint } from './budgetPartyFields';

const RECURRENCE_OPTIONS = ['Mensual', 'Trimestral', 'Semestral', 'Anual', 'Puntual'];
const BUSINESS_UNITS = ['Pinturas', 'Aditivos', 'Servicio', 'Backoffice'];
const UNIT_TONE = { Pinturas: 'info', Aditivos: 'purple', Servicio: 'success', Backoffice: 'warning' };

const emptyForm = (year) => ({
  name: '',
  amount: '',
  recurrence: 'Mensual',
  start_month: '1',
  end_month: '12',
  start_year: String(year || new Date().getFullYear()),
  end_year: String(year || new Date().getFullYear()),
  business_unit: 'Pinturas',
  chart_account_id: '',
  budget_account_id: '',
  notes: '',
  supplier: '',
  payment_term: 'contado',
  splits: [{ budget_account_id: '', chart_account_id: '', share_kind: 'percent', percent: '100', amount: '' }],
});

const parseNumber = (value) => Number(String(value ?? '').replace(',', '.')) || 0;

const emptySplit = (percent = '', amount = '') => ({
  budget_account_id: '',
  chart_account_id: '',
  share_kind: 'percent',
  percent,
  amount,
});

const monthYearLabel = (month, year) => `${MONTH_LABELS[(Number(month) || 1) - 1]} ${year || ''}`.trim();

const yearOptionsFor = (budgetYear, extraYears = []) => {
  const years = new Set();
  const base = Number(budgetYear) || new Date().getFullYear();
  for (let year = base - 5; year <= base + 5; year += 1) years.add(year);
  extraYears.forEach((year) => {
    const value = Number(year);
    if (value) years.add(value);
  });
  return Array.from(years).sort((a, b) => a - b);
};

const accountLeaf = (row) =>
  [
    row.detail_level_6,
    row.detail_level_5,
    row.detail_level_4,
    row.detail_level_3,
    row.detail_level_2,
    row.detail_level_1,
    row.subaccount,
    row.account,
    row.category,
  ]
    .map((value) => String(value || '').trim())
    .find(Boolean) || row.account_code || 'Cuenta';

const periodLabel = (item) => {
  const start = monthYearLabel(item.start_month, item.start_year);
  const end = monthYearLabel(item.end_month, item.end_year);
  if (item.recurrence === 'Puntual') return `${item.recurrence} · ${start}`;
  if (
    Number(item.start_month) === 1 &&
    Number(item.end_month) === 12 &&
    Number(item.start_year) === Number(item.end_year)
  ) {
    return item.start_year ? `${item.recurrence} · ${item.start_year}` : item.recurrence;
  }
  return `${item.recurrence} · ${start}–${end}`;
};

const BudgetCostStructurePanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  chartAccounts = [],
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const budgetAccountOptions = useMemo(() => flattenBudgetAccounts(budgetAccounts), [budgetAccounts]);
  const useBudgetTree = budgetAccountOptions.length > 0;
  const accountOptions = useMemo(
    () =>
      (chartAccounts || [])
        .map((row) => ({
          id: row.id,
          label: `${row.account_code} · ${accountLeaf(row)}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [chartAccounts]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.cost_items || [])
      .filter((item) => (item.schedule_kind || 'recurring') !== 'punctual')
      .filter((item) => {
        if (unitFilter !== 'all' && item.business_unit !== unitFilter) return false;
        if (!q) return true;
        const accounts = (item.allocations || [])
          .map((split) => `${split.account_code || ''} ${split.account_label || ''}`)
          .join(' ');
        return `${item.name} ${item.account_code} ${item.account_label} ${item.group_name} ${item.business_unit} ${item.supplier} ${accounts}`
          .toLowerCase()
          .includes(q);
      });
  }, [budget, search, unitFilter]);

  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const yearOptions = yearOptionsFor(budgetYear, [form.start_year, form.end_year]);
  const itemAmount = parseNumber(form.amount);
  const splitTotals = (form.splits || []).reduce(
    (acc, split) => {
      const amount =
        split.share_kind === 'amount' ? parseNumber(split.amount) : Math.round(itemAmount * parseNumber(split.percent) * 100) / 10000;
      const percent = split.share_kind === 'percent' ? parseNumber(split.percent) : itemAmount ? (parseNumber(split.amount) / itemAmount) * 100 : 0;
      return { amount: acc.amount + amount, percent: acc.percent + percent };
    },
    { amount: 0, percent: 0 }
  );

  const setSplit = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      splits: prev.splits.map((split, current) => (current === index ? { ...split, ...patch } : split)),
    }));
  };

  const addSplit = () => {
    const remainingPct = Math.max(0, Math.round((100 - splitTotals.percent) * 100) / 100);
    setForm((prev) => ({
      ...prev,
      splits: [...prev.splits, emptySplit(remainingPct ? String(remainingPct) : '', remainingPct && itemAmount ? String(Math.round((itemAmount * remainingPct) / 100)) : '')],
    }));
  };

  const removeSplit = (index) => {
    setForm((prev) => ({
      ...prev,
      splits: prev.splits.length <= 1 ? prev.splits : prev.splits.filter((_, current) => current !== index),
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(budgetYear));
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      amount: String(item.amount ?? ''),
      recurrence: item.recurrence || 'Mensual',
      start_month: String(item.start_month || 1),
      end_month: String(item.end_month || 12),
      start_year: String(item.start_year || budgetYear),
      end_year: String(item.end_year || item.start_year || budgetYear),
      business_unit: item.business_unit || 'Pinturas',
      chart_account_id: String(item.chart_account_id || ''),
      budget_account_id: String(item.budget_account_id || ''),
      notes: item.notes || '',
      supplier: item.supplier || '',
      payment_term: item.payment_term || 'contado',
      splits:
        item.allocations && item.allocations.length
          ? item.allocations.map((split) => ({
              budget_account_id: String(split.budget_account_id || ''),
              chart_account_id: String(split.chart_account_id || ''),
              share_kind: split.share_kind || 'percent',
              percent: String(split.percent ?? ''),
              amount: String(split.amount ?? ''),
            }))
          : [
              {
                budget_account_id: String(item.budget_account_id || ''),
                chart_account_id: String(item.chart_account_id || ''),
                share_kind: 'percent',
                percent: '100',
                amount: String(item.amount ?? ''),
              },
            ],
    });
    setError('');
    setShowForm(true);
  };

  const payloadFromForm = () => ({
    name: form.name,
    amount: Number(String(form.amount).replace(',', '.')) || 0,
    schedule_kind: 'recurring',
    recurrence: form.recurrence,
    start_month: Number(form.start_month),
    end_month: Number(form.end_month),
    start_year: Number(form.start_year),
    end_year: Number(form.end_year),
    business_unit: form.business_unit,
    chart_account_id: form.chart_account_id ? Number(form.chart_account_id) : null,
    budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
    notes: form.notes,
    supplier: form.supplier,
    payment_term: form.payment_term || 'contado',
    splits: (form.splits || []).map((split) => ({
      budget_account_id: split.budget_account_id ? Number(split.budget_account_id) : null,
      chart_account_id: split.chart_account_id ? Number(split.chart_account_id) : null,
      share_kind: split.share_kind || 'percent',
      percent: split.share_kind === 'amount' ? null : parseNumber(split.percent),
      amount: split.share_kind === 'percent' ? null : parseNumber(split.amount),
    })),
  });

  const handleSave = async () => {
    if (!budget?.id) return;
    if (Math.abs(splitTotals.percent - 100) > 0.05 && Math.abs(splitTotals.amount - itemAmount) > 0.5) {
      setError('El prorrateo debe sumar 100% o el monto del ítem.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const next = editing
        ? await updateBudgetCostItem(budget.id, editing.id, payload)
        : await addBudgetCostItem(budget.id, payload);
      setShowForm(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el ítem.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.name}?`)) return;
    setBusy(true);
    setError('');
    try {
      const next = await deleteBudgetCostItem(budget.id, item.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el ítem.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Costos recurrentes</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Gastos periódicos con monto conocido: arriendos, sueldos, servicios y otros ítems que se prorratean a cuentas.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar ítem o cuenta…"
            style={{ ...styles.input, width: 260 }}
          />
          <select value={unitFilter} onChange={(event) => setUnitFilter(event.target.value)} style={{ ...styles.input, width: 170 }}>
            <option value="all">Todas las unidades</option>
            {BUSINESS_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Agregar ítem
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
          <thead>
            <tr>
              {['Ítem', 'Unidad', 'Cuenta', 'Periodicidad', 'Monto', 'Anual', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: label === 'Monto' || label === 'Anual' ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{item.name}</div>
                  <PartyHint item={item} ui={ui} />
                  {item.notes ? <div style={{ fontSize: 11, color: ui.muted }}>{item.notes}</div> : null}
                </td>
                <td style={{ padding: 8 }}>
                  <Pill label={item.business_unit || 'Pinturas'} tone={UNIT_TONE[item.business_unit] || 'info'} isDarkMode={isDarkMode} />
                </td>
                <td style={{ padding: 8, fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                  {(item.allocations && item.allocations.length
                    ? item.allocations.map((split) => split.account_label || split.account_code)
                    : [item.account_label || item.account_code]
                  )
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </td>
                <td style={{ padding: 8, fontSize: 12 }}>{periodLabel(item)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{money(item.amount)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{money(item.budget_total)}</td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit ? (
                    <>
                      <button type="button" style={styles.ghostBtn} onClick={() => openEdit(item)}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(item)} disabled={busy}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Agrega gastos recurrentes y clasifícalos a una cuenta para que sumen en el resumen.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar ítem' : 'Nuevo ítem'}
          subtitle="El monto se proyecta según la periodicidad y suma en el estado de resultado."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Nombre
            <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} style={styles.input} />
          </label>
          <label style={styles.label}>
            Unidad de negocio
            <select
              value={form.business_unit}
              onChange={(event) => setForm((prev) => ({ ...prev, business_unit: event.target.value }))}
              style={styles.input}
            >
              {BUSINESS_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Monto por período
            <input value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} style={styles.input} />
          </label>
          <label style={styles.label}>
            Periodicidad
            <select value={form.recurrence} onChange={(event) => setForm((prev) => ({ ...prev, recurrence: event.target.value }))} style={styles.input}>
              {RECURRENCE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Desde
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                <select value={form.start_month} onChange={(event) => setForm((prev) => ({ ...prev, start_month: event.target.value }))} style={styles.input}>
                  {MONTH_LABELS.map((label, index) => (
                    <option key={`start-${label}`} value={String(index + 1)}>
                      {label}
                    </option>
                  ))}
                </select>
                <select value={form.start_year} onChange={(event) => setForm((prev) => ({ ...prev, start_year: event.target.value }))} style={styles.input}>
                  {yearOptions.map((year) => (
                    <option key={`start-year-${year}`} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            {form.recurrence === 'Puntual' ? null : (
              <label style={styles.label}>
                Hasta
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                  <select value={form.end_month} onChange={(event) => setForm((prev) => ({ ...prev, end_month: event.target.value }))} style={styles.input}>
                    {MONTH_LABELS.map((label, index) => (
                      <option key={`end-${label}`} value={String(index + 1)}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select value={form.end_year} onChange={(event) => setForm((prev) => ({ ...prev, end_year: event.target.value }))} style={styles.input}>
                    {yearOptions.map((year) => (
                      <option key={`end-year-${year}`} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 11, color: ui.muted }}>
            El período puede cruzar años. En este presupuesto solo se proyectan los meses de {budgetYear}.
          </p>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: ui.text }}>Clasificación / prorrateo</span>
              <button type="button" style={styles.ghostBtn} onClick={addSplit}>
                <Plus size={13} /> Agregar cuenta
              </button>
            </div>
            {(form.splits || []).map((split, index) => (
              <div key={`split-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 72px 88px auto', gap: 6, marginBottom: 8, alignItems: 'end' }}>
                {useBudgetTree ? (
                  <select
                    value={split.budget_account_id}
                    onChange={(event) => setSplit(index, { budget_account_id: event.target.value, chart_account_id: '' })}
                    style={styles.input}
                  >
                    <option value="">Cuenta</option>
                    {budgetAccountOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.optionLabel}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={split.chart_account_id}
                    onChange={(event) => setSplit(index, { chart_account_id: event.target.value, budget_account_id: '' })}
                    style={styles.input}
                  >
                    <option value="">Cuenta</option>
                    {accountOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  value={split.share_kind}
                  onChange={(event) => {
                    const nextKind = event.target.value;
                    if (nextKind === 'amount') {
                      const amount =
                        parseNumber(split.amount) ||
                        Math.round((itemAmount * parseNumber(split.percent)) / 100);
                      setSplit(index, { share_kind: nextKind, amount: amount ? String(amount) : '' });
                    } else {
                      const percent =
                        parseNumber(split.percent) ||
                        (itemAmount ? Math.round((parseNumber(split.amount) / itemAmount) * 10000) / 100 : 0);
                      setSplit(index, { share_kind: nextKind, percent: percent ? String(percent) : '' });
                    }
                  }}
                  style={styles.input}
                >
                  <option value="percent">%</option>
                  <option value="amount">$</option>
                </select>
                <input
                  value={split.share_kind === 'amount' ? split.amount : split.percent}
                  onChange={(event) =>
                    setSplit(
                      index,
                      split.share_kind === 'amount' ? { amount: event.target.value } : { percent: event.target.value }
                    )
                  }
                  style={{ ...styles.input, textAlign: 'right' }}
                  placeholder={split.share_kind === 'amount' ? 'Monto' : '%'}
                />
                <button type="button" style={styles.ghostBtn} onClick={() => removeSplit(index)} disabled={(form.splits || []).length <= 1}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <p style={{ margin: 0, fontSize: 11, color: Math.abs(splitTotals.percent - 100) > 0.05 && Math.abs(splitTotals.amount - itemAmount) > 0.5 ? ui.danger : ui.muted }}>
              Asignado {splitTotals.percent.toLocaleString('es-CL', { maximumFractionDigits: 2 })}% · {money(splitTotals.amount)}
              {itemAmount ? ` de ${money(itemAmount)}` : ''}.
            </p>
          </div>
          <PartyFields form={form} setForm={setForm} styles={styles} />
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetCostStructurePanel;
